import { taskComponentTypeMatchesRegistryKey, isAlarmEvent, isOpenActivity } from './eventFilters';
import { timestampToMillis } from './readings';
import { severityToKey, conditionStringToLevel } from './conditionMapping';
import { buildLocationMaps, buildModelKeyByComponentId } from './locationPartMapping';

// Only a critical/danger condition recolors the 3D geometry. Warnings (and
// below) are conveyed via floating markers and the surrounding UI, never by
// tinting the component body — a washed-out component reads as a rendering
// glitch rather than a deliberate severity cue.
const TINT_LEVEL_CRITICAL = 3;

export function buildComponentTypeToIds(sceneComponents) {
  const map = {};
  (sceneComponents || []).forEach((comp) => {
    if (!comp || comp.id == null) return;
    const key = comp.component_subtype || 'fallback';
    const registryKey = String(key).toLowerCase();
    if (!map[registryKey]) map[registryKey] = [];
    map[registryKey].push(String(comp.id));
  });
  return map;
}

/** @deprecated prefer resolveRegistryKey in registry.js when components are full scene objects */
export function buildComponentTypeToIdsFromRegistry(sceneComponents, resolveRegistryKey) {
  const map = {};
  (sceneComponents || []).forEach((comp) => {
    const key = resolveRegistryKey(comp);
    if (!map[key]) map[key] = [];
    map[key].push(String(comp.id));
  });
  return map;
}

/**
 * Whether an activity item should drive emissive heatmap tint (not just a marker).
 * Only alarm/event items tint the geometry; tasks and repairs show a floating icon only.
 */
export function eventAppliesHeatmapTint(ev) {
  return isAlarmEvent(ev);
}

// SeverityToHighlightLevel is the single numeric projection on top of classifySeverity
// (via severityToKey). rankSeverity in eventFilters.js has been deleted.
function toMillisOrZero(timestamp) {
  if (!timestamp) return 0;
  const ms = timestampToMillis(timestamp);
  return Number.isNaN(ms) ? 0 : ms;
}

export function severityToHighlightLevel(severity) {
  const key = severityToKey(severity);
  if (key === 'high') return 3;
  if (key === 'medium') return 2;
  // low / info / unknown: a subtle tint, never the critical red pulse.
  return 1;
}

/**
 * Resolve the heatmap level for an activity item. Events carry `severity`
 * (high/medium/low); tasks and repairs instead carry `condition`
 * (warning/critical/...). Prefer a *recognizable* severity, then fall back to
 * the condition string, and only default to level 1 when neither is usable —
 * otherwise a warning/critical task would be read as "unknown" (level 1) and
 * paint the component a washed-out light grey instead of its alarm color.
 *
 * "Recognizable" matters: severityToKey returns 'default' for any string it
 * cannot classify, which is indistinguishable from a genuine low severity. An
 * unclassifiable severity is not an explicit one, so the condition still gets
 * its turn — without this, a critical task whose severity field held any
 * unexpected value resolved to level 1.
 */
export function resolveHighlightLevel(ev) {
  if (ev && ev.severity != null && ev.severity !== '' && severityToKey(ev.severity) !== 'default') {
    return severityToHighlightLevel(ev.severity);
  }
  const conditionLevel = conditionStringToLevel(ev && ev.condition);
  if (conditionLevel != null) return conditionLevel;
  return 1;
}

function resolveComponentId(ev, locationToComponentMap) {
  if (ev.component_id != null) return String(ev.component_id);
  const locKey = ev.location_id != null ? String(ev.location_id) : null;
  if (locKey && locationToComponentMap[locKey]) return locationToComponentMap[locKey];
  return null;
}

function resolvePartId(ev, locationToPartMap) {
  if (ev.part_id) return ev.part_id;
  const locKey = ev.location_id != null ? String(ev.location_id) : null;
  if (locKey && locationToPartMap[locKey]) return locationToPartMap[locKey];
  return null;
}

/**
 * Build highlight entries and floating marker descriptors from liveEvents.
 */
export function buildEventHighlightPlan(
  liveEvents,
  locationToComponentMap,
  locationToPartMap,
  componentTypeToIds
) {
  const entries = [];
  const markers = [];
  // Key: "${componentId}:${group}" where group is 'alarm' | 'task' | 'repair'.
  // This lets task and repair badges coexist on the same component while still
  // keeping only the highest-severity alarm per component in the alarm slot.
  const markersByKey = new Map();
  // Machine-level activities with no component_id and no component_type get a
  // SINGLE badge above the machine centroid instead of one per component.
  // Key: group ('task' | 'repair')
  const machineLevelByGroup = new Map();

  function setMarkerIfHigher(componentId, ev, level, group, eventType) {
    const key = `${componentId}:${group}`;
    const prev = markersByKey.get(key);
    if (!prev || level >= prev.level) {
      markersByKey.set(key, {
        componentId,
        message: ev.title || ev.message,
        eventType,
        level,
      });
    }
  }

  function setMachineLevelIfHigher(ev, level, group, eventType) {
    const prev = machineLevelByGroup.get(group);
    if (!prev || level >= prev.level) {
      machineLevelByGroup.set(group, { message: ev.title || ev.message, eventType, level });
    }
  }

  // Closed/resolved activities are dropped before anything else: the heatmap
  // has no expiry, so a resolved critical alarm would otherwise keep tinting
  // its component red and showing a floating badge indefinitely.
  const sorted = [...(liveEvents || [])].filter(isOpenActivity).sort((a, b) => {
    // Shares readings.js's parser so a numeric timestamp means the same thing
    // on both sides of the feature. This used to read numbers as milliseconds
    // here and as seconds there.
    const ta = toMillisOrZero(a.timestamp);
    const tb = toMillisOrZero(b.timestamp);
    return ta - tb;
  });

  sorted.forEach((ev) => {
    const kind = (ev.type || ev.event_type || '').toLowerCase();
    const isTaskOrRepairKind = kind === 'task' || kind === 'repair';
    const componentId = resolveComponentId(ev, locationToComponentMap);
    const level = resolveHighlightLevel(ev);

    // Floating markers: alarms/events get the red warning icon; tasks and
    // repairs get distinct colored icons (blue for task, green for repair).
    // All alarm/event/warning variants share the 'alarm' slot per component
    // so at most three badges can appear per component: alarm + task + repair.
    if (isAlarmEvent(ev)) {
      if (componentId) setMarkerIfHigher(componentId, ev, level, 'alarm', ev.event_type || ev.type);
    } else if (isTaskOrRepairKind) {
      if (componentId) {
        setMarkerIfHigher(componentId, ev, level, kind, kind);
      } else if (ev.component_type) {
        // Machine-level with known type: badge every matching component.
        Object.entries(componentTypeToIds)
          .filter(([key]) => taskComponentTypeMatchesRegistryKey(ev.component_type, key))
          .forEach(([, ids]) => ids.forEach(cid => setMarkerIfHigher(cid, ev, level, kind, kind)));
      } else {
        // Machine-level with no component_type: one shared badge above the
        // whole machine rather than duplicating it on every component.
        setMachineLevelIfHigher(ev, level, kind, kind);
      }
    }

    // Geometry tint — only a critical alarm condition recolors the model.
    // Tasks and repairs use floating icon markers instead of component tinting.
    if (level !== TINT_LEVEL_CRITICAL) return;
    if (!eventAppliesHeatmapTint(ev)) return;

    if (componentId) {
      entries.push({
        componentId,
        partId: resolvePartId(ev, locationToPartMap) || null,
        level,
      });
    }
  });

  // `level` rides along so the badge can be colored by resolved severity. The
  // eventType alone cannot do it: for alarms it is a human-readable template
  // name ("Rms Acceleration"), which no palette can key off.
  markersByKey.forEach((marker) => {
    markers.push({
      componentId: marker.componentId,
      message: marker.message,
      eventType: marker.eventType,
      level: marker.level,
    });
  });

  const machineLevelMarkers = [];
  machineLevelByGroup.forEach((marker) => {
    machineLevelMarkers.push({
      message: marker.message,
      eventType: marker.eventType,
      level: marker.level,
    });
  });

  return { entries, markers, machineLevelMarkers };
}

/**
 * Apply markers + emissive heatmap tints on a SceneManager instance.
 */
export function applyEventHighlightsToScene(
  sceneManager,
  liveEvents,
  sceneLocations,
  sceneComponents,
  resolveRegistryKey
) {
  if (!sceneManager) return;
  // Re-assert markers rather than clearing first: markers that are still valid
  // keep their existing sprite (and its already-uploaded texture), and only the
  // ones absent from this plan are disposed by pruneEventMarkers() below.
  sceneManager.beginEventMarkerRefresh();

  // The part a location names depends on how its own body tags geometry, so the maps need the
  // resolved model key per component (see locationPartMapping SHAFT_TAGGED_MODEL_KEYS). This is
  // the fallback path only — events enriched at fetch time already carry part_id, which
  // resolvePartId prefers, and that one is keyed the same way in enrichActivityEvents.
  const { locationToComponentMap, locationToPartMap } = buildLocationMaps(
    sceneLocations,
    buildModelKeyByComponentId(sceneComponents, resolveRegistryKey)
  );
  const componentTypeToIds = resolveRegistryKey
    ? buildComponentTypeToIdsFromRegistry(sceneComponents, resolveRegistryKey)
    : buildComponentTypeToIds(sceneComponents);

  const { entries, markers, machineLevelMarkers } = buildEventHighlightPlan(
    liveEvents,
    locationToComponentMap,
    locationToPartMap,
    componentTypeToIds
  );

  markers.forEach(({ componentId, message, eventType, level }) => {
    sceneManager.addLiveEventMarker(componentId, message, eventType, level);
  });
  // `machineLevelMarkers` is deliberately NOT rendered in 3D. A badge's leader
  // line points at the component it describes, so anchoring a machine-wide item
  // above the assembly centroid claimed it belonged to whichever component sat
  // there. Those items are rendered by MachineLevelActivityStrip, which is
  // anchored to the viewport rather than to geometry. The plan still classifies
  // them so they can never leak back into the per-component `markers` list.
  // Drop badges whose events are no longer in the plan, then spread the
  // co-located survivors (alarm + task + repair) horizontally above each component.
  sceneManager.pruneEventMarkers();
  sceneManager.relayoutEventMarkers();
  sceneManager.setEventHighlights(entries, 3);
}
